import React, { useEffect, useId, useRef, useState } from 'react';
import '../../css/ActionMenu.css';

const DOTS = (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
        <circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" />
    </svg>
);

/**
 * ActionMenu — the "⋯" overflow for actions that shouldn't spend toolbar space.
 *
 * A toolbar that shows every action at equal weight shows no hierarchy at all;
 * this is where the rare ones (regenerate a thumbnail, delete) go so the common
 * ones can be buttons.
 *
 * Props:
 *   items — [{ id, label, icon, onSelect, danger, disabled, separatorBefore, description, checked }]
 *           `checked` (true/false) makes the item a menuitemradio — for "pick one" lists
 *           like the workspace switcher. Leave it undefined for plain actions.
 *   align — 'end' (default) | 'start': which edge the menu hangs from
 *   label — names the menu (and, for the default "⋯" trigger, the button too)
 *   trigger — optional button content replacing the "⋯" icon; the button is then
 *             named by that visible content unless `triggerAriaLabel` is given
 *   triggerClassName — replaces the default icon-button classes
 *   header — optional non-interactive block at the top of the menu
 *   focusOnOpen — 'first' (default) | 'checked': in a pick-one list, open onto the
 *                 current choice so arrowing starts from where the user already is
 */
const ActionMenu = ({
    items = [],
    label = 'More actions',
    align = 'end',
    className = '',
    trigger = null,
    triggerClassName = 'qd-btn qd-btn--ghost qd-btn--icon',
    triggerAriaLabel,
    header = null,
    focusOnOpen = 'first',
}) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const buttonRef = useRef(null);
    const itemRefs = useRef([]);
    const menuId = `${useId()}-menu`;

    const enabled = items.filter((item) => !item.disabled);

    useEffect(() => {
        if (!open) return undefined;

        const onPointerDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        const onKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            // Swallow it: the host is usually a modal, and one Escape should
            // close the menu the user just opened, not the dialog behind it.
            e.stopPropagation();
            setOpen(false);
            buttonRef.current?.focus();
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown, true);
        };
    }, [open]);

    const checkedIndex = focusOnOpen === 'checked' ? enabled.findIndex((item) => item.checked === true) : -1;
    useEffect(() => {
        if (open) itemRefs.current[Math.max(checkedIndex, 0)]?.focus();
        // Only on open: re-focusing when the list changes underneath an open
        // menu would yank focus away from the item the user is on.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const select = (item) => {
        setOpen(false);
        buttonRef.current?.focus();
        item.onSelect?.();
    };

    const onItemKeyDown = (e, index) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            itemRefs.current[(index + 1) % enabled.length]?.focus();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            itemRefs.current[(index - 1 + enabled.length) % enabled.length]?.focus();
        } else if (e.key === 'Home') {
            e.preventDefault();
            itemRefs.current[0]?.focus();
        } else if (e.key === 'End') {
            e.preventDefault();
            itemRefs.current[enabled.length - 1]?.focus();
        } else if (e.key === 'Tab') {
            setOpen(false);
        }
    };

    if (items.length === 0) return null;

    let enabledIndex = -1;

    return (
        <div className={`qd-menu-root ${className}`.trim()} ref={rootRef}>
            <button
                ref={buttonRef}
                type="button"
                className={`${triggerClassName} qd-menu-trigger`}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                aria-label={trigger ? triggerAriaLabel : (triggerAriaLabel ?? label)}
                title={trigger ? undefined : label}
                onClick={() => setOpen((v) => !v)}
                onKeyDown={(e) => {
                    if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true); }
                }}
            >
                {trigger ?? DOTS}
            </button>

            {open && (
                <div className={`qd-menu qd-menu--${align}`} id={menuId} role="menu" aria-label={label}>
                    {header && <div className="qd-menu-header">{header}</div>}
                    {items.map((item) => {
                        if (item.disabled) return null;
                        enabledIndex += 1;
                        const index = enabledIndex;
                        return (
                            <React.Fragment key={item.id}>
                                {item.separatorBefore && index > 0 && <div className="qd-menu-sep" role="separator" />}
                                <button
                                    ref={(el) => { itemRefs.current[index] = el; }}
                                    type="button"
                                    role={item.checked === undefined ? 'menuitem' : 'menuitemradio'}
                                    aria-checked={item.checked === undefined ? undefined : item.checked}
                                    className={`qd-menu-item ${item.danger ? 'is-danger' : ''}`.trim()}
                                    onClick={() => select(item)}
                                    onKeyDown={(e) => onItemKeyDown(e, index)}
                                >
                                    {item.icon && <span className="qd-menu-icon" aria-hidden="true">{item.icon}</span>}
                                    <span className="qd-menu-text">
                                        <span className="qd-menu-label">{item.label}</span>
                                        {item.description && <span className="qd-menu-desc">{item.description}</span>}
                                    </span>
                                    {item.checked && (
                                        <svg className="qd-menu-check" viewBox="0 0 24 24" width="16" height="16" fill="none"
                                             stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                                             strokeLinejoin="round" aria-hidden="true">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                    )}
                                </button>
                            </React.Fragment>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ActionMenu;
